import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ChefHat, ArrowRight, ArrowLeft, Check, SkipForward, Sparkles } from "lucide-react";
import TagInput from "@/components/TagInput";
import { TuhaWordmark } from "@/components/TuhaLogo";

const ACTIVITY = [
  { v: "sedentary", l: "Sedentary (little/no exercise)" },
  { v: "light", l: "Light (1-3 days/week)" },
  { v: "moderate", l: "Moderate (3-5 days/week)" },
  { v: "very_active", l: "Very active (6-7 days/week)" },
  { v: "extra_active", l: "Extra active (athlete / 2x day)" },
];
// Richer goal presets. `base` maps to the calorie-direction the backend uses for macros
// (lose/maintain/gain); `condition` (if any) is auto-added to medical conditions so KAAYA's
// coaching becomes condition-aware.
const GOALS = [
  { v: "lose", l: "Lose weight", base: "lose" },
  { v: "shred", l: "Shred fat / get lean", base: "lose" },
  { v: "maintain", l: "Maintain / stay healthy", base: "maintain" },
  { v: "muscle", l: "Build muscle / gain weight", base: "gain" },
  { v: "recomp", l: "Body recomposition", base: "maintain" },
  { v: "pcos", l: "Manage PCOS / PCOD", base: "lose", condition: "PCOS" },
  { v: "thyroid", l: "Manage thyroid", base: "maintain", condition: "thyroid" },
  { v: "diabetes", l: "Manage diabetes / blood sugar", base: "maintain", condition: "diabetes" },
  { v: "cardiac", l: "Heart / cardiac health", base: "maintain", condition: "cardiac health" },
];
const DIET = [{ v: "veg", l: "Vegetarian" }, { v: "non_veg", l: "Non-vegetarian" }, { v: "eggitarian", l: "Eggitarian" }];

const STEPS = ["Basics", "Health", "Tastes"];

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [goalFocus, setGoalFocus] = useState("");
  const [f, setF] = useState({
    age: "", gender: "", height_cm: "", weight_kg: "", goal: "", activity_level: "", dietary_preference: "",
    city: "", region: "", country: "",
    target_weight_kg: "", allergies: [], medical_conditions: [], medications: [],
    cuisines: [], food_preferences: [], preferred_ingredients: [], disliked_foods: [], grocery_sources: [], notes: "",
  });

  useEffect(() => {
    const p = user?.profile;
    if (p) setF((prev) => ({ ...prev, ...Object.fromEntries(Object.entries(p).filter(([, v]) => v != null)) }));
    // Restore the richer goal preset: prefer a condition match, else map the base goal.
    if (p) {
      const cond = (p.medical_conditions || []).map((s) => String(s).toLowerCase());
      const byCond = GOALS.find((g) => g.condition && cond.includes(g.condition.toLowerCase()));
      if (byCond) setGoalFocus(byCond.v);
      else if (p.goal === "gain") setGoalFocus("muscle");
      else if (p.goal) setGoalFocus(p.goal);
    }
  }, [user]);

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  // Selecting a goal preset sets the base calorie goal and (for condition presets) auto-adds
  // the medical condition so the coaching is tailored from the start.
  const pickGoal = (v) => {
    const preset = GOALS.find((g) => g.v === v);
    setGoalFocus(v);
    setF((prev) => {
      const next = { ...prev, goal: preset?.base || "" };
      if (preset?.condition) {
        const have = (prev.medical_conditions || []).map((s) => String(s).toLowerCase());
        if (!have.includes(preset.condition.toLowerCase())) {
          next.medical_conditions = [...(prev.medical_conditions || []), preset.condition];
        }
      }
      return next;
    });
  };

  // Prefill from the landing-page quiz (fresh profiles only), then clear it.
  useEffect(() => {
    if (user?.profile?.goal) return;
    try {
      const raw = localStorage.getItem("tuha_quiz");
      if (!raw) return;
      const q = JSON.parse(raw);
      if (q.preset) pickGoal(q.preset);
      if (q.diet) set("dietary_preference", q.diet);
      if (q.cond_label) {
        setF((prev) => {
          const have = (prev.medical_conditions || []).map((s) => String(s).toLowerCase());
          return have.includes(q.cond_label.toLowerCase())
            ? prev
            : { ...prev, medical_conditions: [...(prev.medical_conditions || []), q.cond_label] };
        });
      }
      localStorage.removeItem("tuha_quiz");
    } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const buildPayload = () => ({
    age: f.age ? Number(f.age) : null,
    gender: f.gender || null,
    height_cm: f.height_cm ? Number(f.height_cm) : null,
    weight_kg: f.weight_kg ? Number(f.weight_kg) : null,
    target_weight_kg: f.target_weight_kg ? Number(f.target_weight_kg) : null,
    goal: f.goal || null,
    activity_level: f.activity_level || null,
    dietary_preference: f.dietary_preference || null,
    city: f.city || null, region: f.region || null, country: f.country || null,
    allergies: f.allergies || [], medical_conditions: f.medical_conditions || [], medications: f.medications || [],
    cuisines: f.cuisines || [], food_preferences: f.food_preferences || [],
    preferred_ingredients: f.preferred_ingredients || [], disliked_foods: f.disliked_foods || [],
    grocery_sources: f.grocery_sources || [], notes: f.notes || "",
  });

  const persist = async (silent = false) => {
    setSaving(true);
    try {
      await api.put("/profile", buildPayload());
      await refresh();
      if (!silent) toast.success("Saved");
      return true;
    } catch (e) {
      toast.error("Couldn't save — please try again");
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Mandatory basics MUST match the backend gate (profile_completeness) so the wizard can
  // never let you continue while the gate would still bounce you back. Diet + location are
  // recommended but optional, so they never block access.
  const basicsComplete = f.age && f.gender && f.height_cm && f.weight_kg && f.goal && f.activity_level;

  const next = async () => {
    if (step === 0) {
      if (!basicsComplete) return toast.warning("Please complete all required basics to continue");
      const ok = await persist(true);
      if (!ok) return;
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };

  const finish = async () => {
    if (!basicsComplete) { setStep(0); return toast.warning("Complete the required basics first"); }
    const ok = await persist(true);
    if (ok) { toast.success("You're all set!"); nav("/chat"); }
  };

  const finishLater = async () => {
    await persist(true);
    nav(basicsComplete ? "/chat" : "/chat"); // gate will re-prompt if basics still missing
  };

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <TuhaWordmark size="sm" />
          <button onClick={finishLater} className="text-sm text-muted-foreground hover:text-foreground" data-testid="onb-finish-later">Finish later</button>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-10">
        <div className="text-overline">welcome — let's set you up</div>
        <h1 className="font-serif-display text-4xl md:text-5xl text-primary tracking-tight mb-2">Tell us about you</h1>
        <p className="text-foreground/70 mb-6">Step 1 is required so KAAYA can personalize evidence-informed, condition-aware nutrition for you. The rest is optional — skip anything and add it later.</p>

        {/* Stepper */}
        <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="onb-stepper">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${i === step ? "bg-primary text-primary-foreground" : i < step ? "bg-secondary/15 text-secondary" : "bg-surface text-muted-foreground"}`}>
                {i < step ? <Check size={14} /> : <span className="font-medium">{i + 1}</span>} {s}
              </div>
              {i < STEPS.length - 1 && <div className="w-6 h-px bg-border" />}
            </div>
          ))}
        </div>

        <div className="bg-surface-elevated border border-border rounded-3xl p-6">
          {step === 0 && (
            <div className="space-y-4" data-testid="onb-step-basics">
              <div className="text-overline">required basics</div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Age *</Label><Input type="number" value={f.age} onChange={(e) => set("age", e.target.value)} data-testid="onb-age" /></div>
                <div><Label>Gender *</Label>
                  <Select value={f.gender} onValueChange={(v) => set("gender", v)}>
                    <SelectTrigger data-testid="onb-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Height (cm) *</Label><Input type="number" value={f.height_cm} onChange={(e) => set("height_cm", e.target.value)} data-testid="onb-height" /></div>
                <div><Label>Weight (kg) *</Label><Input type="number" value={f.weight_kg} onChange={(e) => set("weight_kg", e.target.value)} data-testid="onb-weight" /></div>
                <div><Label>Goal *</Label>
                  <Select value={goalFocus} onValueChange={pickGoal}>
                    <SelectTrigger data-testid="onb-goal"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{GOALS.map((g) => <SelectItem key={g.v} value={g.v}>{g.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Activity level *</Label>
                  <Select value={f.activity_level} onValueChange={(v) => set("activity_level", v)}>
                    <SelectTrigger data-testid="onb-activity"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{ACTIVITY.map((a) => <SelectItem key={a.v} value={a.v}>{a.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Diet</Label>
                  <Select value={f.dietary_preference} onValueChange={(v) => set("dietary_preference", v)}>
                    <SelectTrigger data-testid="onb-diet"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{DIET.map((d) => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="text-overline pt-2">location <span className="text-muted-foreground normal-case tracking-normal">— recommended: powers locally available & seasonal ingredients</span></div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><Label>City</Label><Input value={f.city} onChange={(e) => set("city", e.target.value)} placeholder="Your city" data-testid="onb-city" /></div>
                <div><Label>State / region</Label><Input value={f.region} onChange={(e) => set("region", e.target.value)} placeholder="Your state / region" data-testid="onb-region" /></div>
                <div><Label>Country</Label><Input value={f.country} onChange={(e) => set("country", e.target.value)} placeholder="Your country" data-testid="onb-country" /></div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4" data-testid="onb-step-health">
              <div className="text-overline">health & goals <span className="text-muted-foreground normal-case tracking-normal">— optional</span></div>
              <div><Label>Target weight (kg)</Label><Input type="number" value={f.target_weight_kg} onChange={(e) => set("target_weight_kg", e.target.value)} data-testid="onb-target" /></div>
              <div><Label>Allergies</Label><TagInput value={f.allergies} onChange={(v) => set("allergies", v)} placeholder="peanuts, shellfish…" testid="onb-allergies" /></div>
              <div><Label>Medical conditions</Label><TagInput value={f.medical_conditions} onChange={(v) => set("medical_conditions", v)} placeholder="diabetic, PCOS, hypertension…" testid="onb-conditions" /></div>
              <div><Label>Medications</Label><TagInput value={f.medications} onChange={(v) => set("medications", v)} placeholder="metformin, thyroxine…" testid="onb-meds" /></div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="onb-step-tastes">
              <div className="text-overline">tastes & preferences <span className="text-muted-foreground normal-case tracking-normal">— optional</span></div>
              <div><Label>Cuisines you love</Label><TagInput value={f.cuisines} onChange={(v) => set("cuisines", v)} placeholder="South Indian, Mediterranean…" testid="onb-cuisines" /></div>
              <div><Label>Food preferences</Label><TagInput value={f.food_preferences} onChange={(v) => set("food_preferences", v)} placeholder="spicy, low-sodium, high-protein…" testid="onb-foodprefs" /></div>
              <div><Label>Preferred ingredients</Label><TagInput value={f.preferred_ingredients} onChange={(v) => set("preferred_ingredients", v)} placeholder="olive oil, paneer, lentils…" testid="onb-ingredients" /></div>
              <div><Label>Disliked foods</Label><TagInput value={f.disliked_foods} onChange={(v) => set("disliked_foods", v)} placeholder="bell peppers, mushrooms…" testid="onb-dislikes" /></div>
              <div><Label>Grocery sources</Label><TagInput value={f.grocery_sources} onChange={(v) => set("grocery_sources", v)} placeholder="BigBasket, BlinkIt…" testid="onb-grocery" /></div>
              <div><Label>Anything else?</Label><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} className="rounded-xl bg-surface" data-testid="onb-notes" /></div>
            </div>
          )}

          {step === 0 && (
            <p className="text-xs text-muted-foreground mt-5 text-center">
              You can get started right away — add health details & tastes anytime later from your profile.
            </p>
          )}

          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 mt-6 pt-4 border-t border-border">
            <Button variant="ghost" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))} className="rounded-full shrink-0 w-full sm:w-auto" data-testid="onb-back"><ArrowLeft size={16} className="mr-1" /> Back</Button>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
              {step === 0 ? (
                <>
                  <Button variant="outline" onClick={next} disabled={saving} className="rounded-full w-full sm:w-auto" data-testid="onb-deepdive"><Sparkles size={16} className="mr-1" /> Deep dive — add more</Button>
                  <Button onClick={finish} disabled={saving} className="rounded-full bg-primary hover:bg-primary/90 w-full sm:w-auto" data-testid="onb-getstarted">{saving ? "Saving…" : "Get started"} <ArrowRight size={16} className="ml-1" /></Button>
                </>
              ) : (
                <>
                  {step < STEPS.length - 1 && (
                    <Button variant="outline" onClick={() => setStep((s) => s + 1)} className="rounded-full w-full sm:w-auto" data-testid="onb-skip"><SkipForward size={16} className="mr-1" /> Skip</Button>
                  )}
                  {step < STEPS.length - 1 ? (
                    <Button onClick={next} disabled={saving} className="rounded-full bg-primary hover:bg-primary/90 w-full sm:w-auto" data-testid="onb-next">{saving ? "Saving…" : "Save & continue"} <ArrowRight size={16} className="ml-1" /></Button>
                  ) : (
                    <Button onClick={finish} disabled={saving} className="rounded-full bg-primary hover:bg-primary/90 w-full sm:w-auto" data-testid="onb-finish">{saving ? "Saving…" : "Finish setup"} <Check size={16} className="ml-1" /></Button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
